import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const UPDATED = 'February 2026';

export default function Privacy() {
  return (
    <div className="bg-[#0a0a0b] text-zinc-100 min-h-screen">
      <SEO
        title="Privacy Policy — Reachly"
        description="How Reachly collects, uses, stores, and shares your data — including data from your connected Google or Microsoft account."
      />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-16 pb-24">
        <div className="text-sm uppercase tracking-widest text-[#d4a94a]">Legal</div>
        <h1 className="mt-3 font-display text-5xl md:text-6xl">Privacy Policy</h1>
        <div className="mt-3 text-sm text-zinc-500">Last updated: {UPDATED}</div>

        <article className="prose prose-invert prose-zinc max-w-none mt-10 prose-headings:font-display prose-a:text-[#f5d67a]">
          <p>
            Reachly ("we", "us", or "the Service") helps you run personalised email
            outreach campaigns using your own email account. This policy explains
            what personal data we collect, how we use it, and the choices you have.
            Contact us at <a href="mailto:hello@ireachly.com">hello@ireachly.com</a> with any questions.
          </p>

          <h2>1. Information we collect</h2>
          <ul>
            <li><strong>Account information</strong> you provide directly: name, email, hashed password, timezone, and — if you upgrade — billing metadata.</li>
            <li><strong>Google or Microsoft account data</strong> if you connect Gmail or Outlook: your email address, a durable account identifier, and OAuth tokens that let us send email from your account and check for replies. We never ask for or store your Google/Microsoft password.</li>
            <li><strong>Campaign data</strong> you create: contacts you upload, campaign templates, follow-up schedules, and delivery / reply metadata.</li>
            <li><strong>Product usage</strong>: pages visited, actions taken, and error logs, used to keep the service reliable.</li>
          </ul>

          <h2>2. How we use Google user data</h2>
          <p>
            When you connect Gmail, we request the <code>gmail.send</code> and
            <code> gmail.readonly</code> scopes. We use this access <strong>only</strong> to:
          </p>
          <ul>
            <li>Send emails you author on your behalf.</li>
            <li>Read message metadata (headers, thread IDs) and the message list <em>within threads Reachly created</em> to detect replies and auto-pause a sequence.</li>
          </ul>
          <p>
            Reachly's use and transfer of information received from Google APIs
            adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>,
            including the Limited Use requirements. Specifically we do <strong>not</strong>:
          </p>
          <ul>
            <li>Use Google user data to train generalized AI or ML models.</li>
            <li>Serve advertising, or transfer Google user data to third parties for advertising.</li>
            <li>Allow humans to read Google user data unless (a) we have your explicit consent for specific messages, (b) it is necessary for security purposes like investigating abuse, (c) it is required to comply with applicable law, or (d) the data has been aggregated and anonymised.</li>
          </ul>

          <h2>3. How we use Microsoft user data</h2>
          <p>
            When you connect Outlook we request the <code>Mail.Send</code> and
            <code> Mail.ReadWrite</code> scopes. We use this access with the same
            constraints as Google data above: only to send campaign emails you
            authored and to detect replies in threads Reachly created.
          </p>

          <h2>4. Storage and retention</h2>
          <ul>
            <li>OAuth refresh tokens are encrypted at rest.</li>
            <li>Contact and campaign data live in our database for as long as your account is active. You can delete a campaign or contact at any time.</li>
            <li>If you delete your account, we remove your data within 30 days, except where retention is required to resolve disputes, prevent fraud, or comply with law.</li>
          </ul>

          <h2>5. Sharing</h2>
          <p>
            We do not sell your data. We share it only with subprocessors necessary
            to operate the service (hosting, email delivery, payment processing).
            All subprocessors are bound by data-processing agreements.
          </p>

          <h2>6. Your choices</h2>
          <ul>
            <li><strong>Disconnect Google/Microsoft</strong>: revoke Reachly's access anytime from your Google or Microsoft security settings, or from Reachly's <Link to="/app/settings">Settings</Link> page. This immediately deletes the stored refresh token.</li>
            <li><strong>Export or delete</strong>: email <a href="mailto:hello@ireachly.com">hello@ireachly.com</a> to request an export or deletion of your account data.</li>
            <li><strong>Marketing</strong>: we don't send you marketing email without your consent. Transactional email (password resets, billing) is required for the service to function.</li>
          </ul>

          <h2>7. Security</h2>
          <p>
            We use HTTPS across the service, hash passwords with bcrypt, encrypt
            refresh tokens at rest, and follow the principle of least privilege
            for internal access. No system is perfectly secure — please report
            suspected vulnerabilities to <a href="mailto:hello@ireachly.com">hello@ireachly.com</a>.
          </p>

          <h2>8. Children</h2>
          <p>Reachly is not intended for users under 16. We do not knowingly collect data from children.</p>

          <h2>9. Changes to this policy</h2>
          <p>
            When we change this policy we will update the "Last updated" date
            above and, for material changes, notify you by email or in-app
            before the change takes effect.
          </p>

          <h2>10. Contact</h2>
          <p>
            Reachly · <a href="mailto:hello@ireachly.com">hello@ireachly.com</a>
          </p>
        </article>
      </section>
    </div>
  );
}
